import * as React from 'react'

import { observer } from 'mobx-react'

import AccessGroup from 'modules/core/store/models/AccessGroup'

import {
  ParticipantsDynamicSection,
  ParticipantsMembersSection,
} from 'modules/surveys/components/Participants/ParticipantsSections'
import { SurveyParticipantsContext } from 'modules/surveys/pages/Show/Participants/hooks'
import { useSurveyShowContext } from 'modules/surveys/pages/Show/hooks'
import type SurveyForm from 'modules/surveys/store/models/Form'
import type Survey from 'modules/surveys/store/models/v2/Survey'

interface Props {
  form: SurveyForm | Survey
  onSubmit: () => void
  canEditSurvey: boolean
}

function isMembers(accessGroup: AccessGroup): boolean {
  return accessGroup.attributes.strategy === AccessGroup.Strategies.members
}

const Participants = ({ form, onSubmit, canEditSurvey }: Props) => {
  const { accessGroup } = React.useContext(SurveyParticipantsContext)
  const { survey, hasSurveyV2 } = useSurveyShowContext()

  if (!accessGroup) {
    throw new Error(`Missing AccessGroup in SurveyForm with id: ${form.attributes.id}`)
  }

  const isMembersGroups = isMembers(accessGroup)
  const allowAddParticipants = () => {
    if (hasSurveyV2 && survey) {
      if (survey.isActive) return true
      // If the survey is draft and the access group is a members group allow to add participants
      // as it will create access group memberships. Otherwise, disallow adding participants
      if (survey.isDraft && isMembersGroups) return true
      return false
    } else {
      return canEditSurvey && !form.isDraft
    }
  }

  return (
    <>
      {isMembersGroups ? (
        <ParticipantsMembersSection
          accessGroup={accessGroup}
          surveyId={form.attributes.id}
          allowAddParticipants={allowAddParticipants()}
        />
      ) : (
        <ParticipantsDynamicSection
          accessGroup={accessGroup}
          form={form}
          onSubmit={onSubmit}
          allowAddParticipants={allowAddParticipants()}
          canEditSurvey={canEditSurvey}
        />
      )}
    </>
  )
}

export default observer(Participants)
